import { ago } from './format'
import type {
  DeskEntry,
  DeskGuest,
  DeskItem,
  ExpectedGuest,
  StorageLocation,
  Unit,
} from '@/api/pending'

export const DESK_CARRIERS = [
  'Amazon',
  'Mercado Livre',
  'Correios',
  'Shopee',
  'Magazine Luiza',
  'iFood',
  'Outra transportadora',
] as const

/** Carriers offered in the register modal: the list above plus Carta. */
export const CARRIER_CHOICES = [...DESK_CARRIERS, 'Carta'] as const

export const CARTA = 'Carta'

export const ITEM_LABEL = { package: 'Encomenda', letter: 'Carta' } as const

export const STORAGE_LOCATIONS: { id: StorageLocation; label: string }[] = [
  { id: 'front_desk', label: 'Portaria' },
  { id: 'parcel_room', label: 'Sala de encomendas' },
  { id: 'locker', label: 'Armário' },
]

export const MAX_PHOTOS = 4

/** "Portaria", "Sala de encomendas" or "Armário 12". */
export function storageLabel(location: StorageLocation, slot?: string): string {
  if (location === 'locker') return slot ? `Armário ${slot}` : 'Armário'
  return STORAGE_LOCATIONS.find((l) => l.id === location)?.label ?? ''
}

export interface Batch {
  kind: 'batch'
  id: string
  unit: string
  tower: string
  resident: string
  pickup_code: string
  items: DeskItem[]
  status: 'pending'
  oldest_at: string
  newest_at: string
  flagged: boolean
  count: number
}

export const isWaiting = (e: DeskEntry): boolean => e.status === 'pending'

export const deskTitle = (e: DeskEntry): string =>
  e.kind === 'guest' ? e.guest_name : e.item_type === 'letter' ? 'Carta' : `Encomenda ${e.carrier}`

/** Waiting items collapse into one batch per unit: the row the doorman acts on, oldest first. */
export function deskBatches(desk: DeskEntry[]): Batch[] {
  const byUnit = new Map<string, DeskItem[]>()
  for (const e of desk) {
    if (e.kind !== 'item' || !isWaiting(e)) continue
    byUnit.set(e.unit, [...(byUnit.get(e.unit) ?? []), e])
  }
  return [...byUnit.values()]
    .map((list): Batch => {
      const items = [...list].sort((x, y) => y.created_at.localeCompare(x.created_at))
      const first = items[0]!
      return {
        kind: 'batch',
        id: `batch-${first.unit}`,
        unit: first.unit,
        tower: first.tower,
        resident: first.resident,
        pickup_code: first.pickup_code,
        items,
        status: 'pending',
        newest_at: first.created_at,
        oldest_at: items[items.length - 1]!.created_at,
        flagged: items.some((i) => i.flagged),
        count: items.length,
      }
    })
    .sort((a, b) => a.oldest_at.localeCompare(b.oldest_at))
}

/** Guests that arrived at the lobby and still wait for the resident's code. */
export const lobbyGuests = (desk: DeskEntry[]): DeskGuest[] =>
  desk.filter((e): e is DeskGuest => e.kind === 'guest' && isWaiting(e))

/** "mais antigo há 6d", only for batches with more than one item (a single item has no "oldest"). */
export function oldestSubline(b: Pick<Batch, 'count' | 'oldest_at'>, now?: number): string | null {
  return b.count > 1 ? `mais antigo ${ago(b.oldest_at, now)}` : null
}

export function batchSummary(b: Batch, now?: number): string {
  const line = oldestSubline(b, now)
  return `${b.count} ${b.count > 1 ? 'itens' : 'item'}${line ? ` · ${line}` : ''}`
}

/** "Mercado Livre ×2 · Correios" */
export function carrierBreakdown(b: Pick<Batch, 'items'>): string {
  const counts = new Map<string, number>()
  for (const i of b.items) {
    const key = i.item_type === 'letter' ? CARTA : i.carrier
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return [...counts].map(([k, n]) => (n > 1 ? `${k} ×${n}` : k)).join(' · ')
}

/* ---- Register queue ---- */

export interface QueueLine {
  id: string
  unit: Unit
  carrier: string
  count: number
}

/** Adds a line to the queue; the same unit + carrier sums the counts instead of adding a row. */
export function addToQueue(queue: QueueLine[], line: Omit<QueueLine, 'id'>): QueueLine[] {
  const i = queue.findIndex((l) => l.unit.id === line.unit.id && l.carrier === line.carrier)
  if (i > -1) {
    return queue.map((l, idx) => (idx === i ? { ...l, count: l.count + line.count } : l))
  }
  return [...queue, { ...line, id: `${line.unit.id}-${line.carrier}` }]
}

export const queueTotal = (queue: QueueLine[]): number => queue.reduce((s, l) => s + l.count, 0)

export interface RegistrationExtras {
  storage_location: StorageLocation
  locker_slot?: string
  photo_upload_ids?: string[]
}

export interface UnitRegistration {
  unit: Unit
  items: ({ item_type: 'package' | 'letter'; carrier: string } & Partial<RegistrationExtras>)[]
}

/**
 * One registration per unit (one `POST …/receivings/items` each): a line becomes `count`
 * items with its carrier; lines for the same unit with different carriers share the call.
 * (The prototype's `registerArrival` read `p.unit` on a payload that only had `lines`.)
 */
export function groupByUnit(
  lines: Pick<QueueLine, 'unit' | 'carrier' | 'count'>[],
  extras?: RegistrationExtras,
): UnitRegistration[] {
  const byUnit = new Map<number, UnitRegistration>()
  for (const line of lines) {
    const reg = byUnit.get(line.unit.id) ?? { unit: line.unit, items: [] }
    for (let n = 0; n < line.count; n++) {
      reg.items.push({
        item_type: line.carrier === CARTA ? 'letter' : 'package',
        carrier: line.carrier,
        ...(extras && {
          storage_location: extras.storage_location,
          ...(extras.storage_location === 'locker' && extras.locker_slot
            ? { locker_slot: extras.locker_slot }
            : {}),
          ...(extras.photo_upload_ids?.length ? { photo_upload_ids: extras.photo_upload_ids } : {}),
        }),
      })
    }
    byUnit.set(line.unit.id, reg)
  }
  return [...byUnit.values()]
}

const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

/** 6-char pickup code without look-alike characters. */
export function generateCode(random: () => number = Math.random): string {
  return Array.from({ length: 6 }, () => CODE_ALPHABET[Math.floor(random() * 32)]).join('')
}

/**
 * Applies a registration to a desk list: items join the unit's open batch (reusing its pickup
 * code), else get a freshly generated one. Used by the mock server; the real API does the same.
 */
export function applyRegistration(
  desk: DeskEntry[],
  reg: UnitRegistration,
  opts: { now?: string; random?: () => number } = {},
): DeskEntry[] {
  const now = opts.now ?? new Date().toISOString()
  const open = desk.find(
    (e): e is DeskItem => e.kind === 'item' && isWaiting(e) && e.unit === reg.unit.number,
  )
  const code = open?.pickup_code ?? generateCode(opts.random)
  const created: DeskItem[] = reg.items.map((it, idx) => ({
    kind: 'item',
    id: `i-${Date.parse(now)}-${reg.unit.id}-${idx}`,
    item_type: it.item_type,
    carrier: it.carrier,
    photo: !!it.photo_upload_ids?.length,
    ...(it.storage_location && { storage_location: it.storage_location }),
    ...(it.locker_slot && { locker_slot: it.locker_slot }),
    unit: reg.unit.number,
    tower: reg.unit.tower,
    resident: reg.unit.residents[0]?.name ?? 'Sem morador vinculado',
    pickup_code: code,
    status: 'pending',
    created_at: now,
  }))
  return [...created, ...desk]
}

/* ---- Desk view helpers ---- */

const norm = (s: string | undefined) => (s ?? '').toLowerCase()

/** Search over title, unit, resident and tower (history and lobby rows). */
export function matchesEntry(e: DeskEntry, q: string): boolean {
  const t = norm(q).trim()
  return !t || [deskTitle(e), e.unit, e.resident, e.tower].some((f) => norm(f).includes(t))
}

/** Search over unit, resident, tower and the carrier breakdown (batch rows). */
export function matchesBatch(b: Batch, q: string): boolean {
  const t = norm(q).trim()
  return !t || [b.unit, b.resident, b.tower, carrierBreakdown(b)].some((f) => norm(f).includes(t))
}

export function matchesExpected(g: ExpectedGuest, q: string): boolean {
  const t = norm(q).trim()
  return !t || [g.guest_name, g.unit, g.resident, g.tower].some((f) => norm(f).includes(t))
}

export function deskStatusLabel(e: DeskEntry | Batch): string {
  if (e.kind === 'batch') return 'Aguardando'
  if (e.kind === 'guest') {
    return e.status === 'checked_in' ? 'Entrada confirmada' : 'Na portaria'
  }
  return e.status === 'picked_up' ? 'Entregue' : 'Aguardando'
}

export function deskStatusTone(e: DeskEntry | Batch): 'pending' | 'approved' | 'neutral' {
  if (e.kind === 'batch') return 'pending'
  if (e.status === 'checked_in') return 'approved'
  if (e.status === 'picked_up') return 'neutral'
  return 'pending'
}

/** Unit picker filter: number prefix, tower, or any resident name (substring). */
export function filterUnits(units: Unit[], q: string): Unit[] {
  const t = q.trim().toLowerCase()
  if (!t) return units
  return units.filter(
    (u) =>
      u.number.startsWith(t) ||
      u.tower.toLowerCase().includes(t) ||
      u.residents.some((r) => r.name.toLowerCase().includes(t)),
  )
}

/** Local calendar date as `YYYY-MM-DD`. */
export function todayISO(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
}

/** Expected guests whose visit is for the given unit (number + tower). */
export const expectedForUnit = (expected: ExpectedGuest[], unit: Unit | null): ExpectedGuest[] =>
  unit ? expected.filter((g) => g.unit === unit.number && g.tower === unit.tower) : []

export const itemsLabel = (n: number): string => `${n} ${n > 1 ? 'itens' : 'item'}`
